// The in-memory API's Chief of Staff (COS-SLICE1; services/api/src/routes/cos.ts): the loop's
// settings and the per-DE digest switch (`chiefOfStaff.get` / `.put`, managers only), and the
// follow-up cards under chat messages (`suggestions.list` / `.accept` / `.dismiss`). The twin
// holds no detector: cards exist only when a test seeds one (`FakeApi.suggest`), so a demo
// company shows none (no sample data). Accepting marks the card and names a result; the task,
// reminder or meeting itself is the real API's.
import type { OperationInput, OperationOutput, RouteId } from "@companyos/sdk";
import type { Handler, Principal } from "./fake";
import { json, notFound, problem, validation } from "./fake";
import type { FakeDe } from "./fake-data";

type Suggestion = OperationOutput<"suggestions.accept">;
type CosView = OperationOutput<"chiefOfStaff.get">;
type CosSettings = CosView["settings"];
type CosPut = OperationInput<"chiefOfStaff.put">["body"];
type Accept = OperationInput<"suggestions.accept">["body"];

/** services/api/src/domain/cos/rules.ts `COS_DEFAULTS`. */
export const FAKE_COS_DEFAULTS: CosSettings = {
  interval_min: 15,
  max_runs_per_hour: 6,
  max_runs_per_day: 40,
  quiet_hours: { from: "19:00", to: "08:00", weekends: true },
  approval_wait_hours: 4,
  max_effects_per_run: 10,
  suggestions: true,
};

/** The chat-gated routes of this twin (`feature: chat`). */
export const FAKE_COS_CHAT_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  "suggestions.list",
  "suggestions.accept",
  "suggestions.dismiss",
]);
/** The agents-gated routes of this twin (`feature: agents`). */
export const FAKE_COS_AGENTS_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  "chiefOfStaff.get",
  "chiefOfStaff.put",
]);

/** What a test seeds: the card's place and kind; the rest is filled in. */
export type FakeSuggestionSeed = {
  message_id: string;
  thread_id: string;
  channel_id: string;
  kind: Suggestion["kind"];
  proposal: Suggestion["proposal"];
  defaults?: Partial<Suggestion["defaults"]>;
};

type DigestState = { enabled: boolean; channel_id: string | null };

export function createCosFake(deps: {
  now: () => Date;
  desOf: (companyId: string) => FakeDe[];
  /** The channels a DE could post its digest in (the team channel, when there is one). */
  channelsOf: (companyId: string) => { id: string; name: string }[];
}) {
  const settings = new Map<string, CosSettings>();
  const digests = new Map<string, DigestState>();
  const cards = new Map<string, Suggestion[]>();

  const personOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id && m.kind === "person") ?? null;

  function view(p: Principal): CosView {
    const co = p.company.id;
    const channels = deps.channelsOf(co).map((c) => ({ ...c, is_private: false }));
    return {
      settings: settings.get(co) ?? FAKE_COS_DEFAULTS,
      des: deps.desOf(co).map((d) => {
        const s = digests.get(d.membership_id) ?? { enabled: false, channel_id: null };
        return {
          membership_id: d.membership_id,
          name: d.display_name,
          enabled: s.enabled,
          channel_id: s.channel_id,
          channels,
          last_check_at: null,
          last_digest_at: null,
          last_digest_read_at: null,
          runs_last_hour: 0,
          runs_last_day: 0,
        };
      }),
    };
  }

  function find(p: Principal, id: string): Suggestion | null {
    return (cards.get(p.company.id) ?? []).find((s) => s.id === id) ?? null;
  }

  function decide(p: Principal, s: Suggestion, patch: Partial<Suggestion>): Suggestion {
    Object.assign(s, {
      decided_by_membership_id: personOf(p)?.membership_id ?? null,
      decided_at: deps.now().toISOString(),
      ...patch,
    });
    return s;
  }

  const handlers: Partial<Record<RouteId, Handler>> = {
    "chiefOfStaff.get": ({ principal }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      return json(view(principal));
    },
    "chiefOfStaff.put": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      const b = body as CosPut | null;
      if (!b?.settings || !Array.isArray(b.des)) return validation("settings and des are required");
      const s = b.settings;
      if (s.interval_min < 5 || s.interval_min > 60)
        return validation("settings.interval_min: between 5 and 60");
      settings.set(principal.company.id, { ...s });
      const mine = new Set(deps.desOf(principal.company.id).map((d) => d.membership_id));
      for (const d of b.des) {
        if (!mine.has(d.membership_id)) return validation(`des: no DE ${d.membership_id}`);
        digests.set(d.membership_id, { enabled: d.enabled, channel_id: d.channel_id });
      }
      return json(view(principal));
    },
    "suggestions.list": ({ principal, params }) => {
      if (!principal) return notFound();
      const data = (cards.get(principal.company.id) ?? []).filter(
        (s) => s.thread_id === params.id && s.state === "pending",
      );
      return json({ data });
    },
    "suggestions.accept": ({ principal, params, body }) => {
      if (!principal) return notFound();
      const s = find(principal, params.id ?? "");
      if (!s) return notFound();
      if (s.state !== "pending")
        return problem(
          409,
          "suggestion_decided",
          "This suggestion was already accepted or dismissed",
        );
      const b = body as Accept | null;
      if (!b || b.kind !== s.kind) return validation("kind: must be the card's kind");
      return json(
        decide(principal, s, {
          state: "accepted",
          result_kind: s.kind,
          result_id: crypto.randomUUID(),
        }),
      );
    },
    "suggestions.dismiss": ({ principal, params }) => {
      if (!principal) return notFound();
      const s = find(principal, params.id ?? "");
      if (!s) return notFound();
      if (s.state !== "pending")
        return problem(
          409,
          "suggestion_decided",
          "This suggestion was already accepted or dismissed",
        );
      return json(decide(principal, s, { state: "dismissed" }));
    },
  };

  /** Test hook: a pending card under a message, written as the company's first DE. */
  function suggest(companyId: string, seed: FakeSuggestionSeed): Suggestion {
    const now = deps.now();
    const s: Suggestion = {
      id: crypto.randomUUID(),
      message_id: seed.message_id,
      thread_id: seed.thread_id,
      channel_id: seed.channel_id,
      kind: seed.kind,
      state: "pending",
      source: "rule",
      proposal: seed.proposal,
      created_by_membership_id: deps.desOf(companyId)[0]?.membership_id ?? crypto.randomUUID(),
      decided_by_membership_id: null,
      decided_at: null,
      result_kind: null,
      result_id: null,
      expires_at: new Date(now.getTime() + 7 * 86_400_000).toISOString(),
      created_at: now.toISOString(),
      defaults: {
        board_id: seed.defaults?.board_id ?? null,
        meeting_series_id: seed.defaults?.meeting_series_id ?? null,
      },
    };
    cards.set(companyId, [...(cards.get(companyId) ?? []), s]);
    return s;
  }

  return { handlers, suggest };
}
